package com.rosario.juridico.domain

import com.rosario.juridico.marketplace.model.NivelVerificacion
import com.rosario.juridico.platform.FirebaseProxy
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.datetime.Clock

sealed class OnboardingResult {
    object Success : OnboardingResult()
    data class Error(val message: String) : OnboardingResult()
}

class OnboardingRepository {
    suspend fun actualizarRolUsuario(
        rol: String,
        matricula: String = "",
        telefono: String = ""
    ): OnboardingResult = withContext(Dispatchers.Default) {
        val currentUser = Firebase.auth.currentUser
        if (currentUser == null || currentUser.isAnonymous) {
            println("Error: No hay usuario autenticado al intentar guardar.")
            return@withContext OnboardingResult.Error("Sesión no válida o no autenticada en Firebase. Inicia sesión con tu cuenta.")
        }
        val uid = currentUser.uid

        try {
            val db = Firebase.firestore
            val email = currentUser.email ?: FirebaseProxy.getUserEmail() ?: ""
            val now = Clock.System.now().toEpochMilliseconds().toDouble()
            val isAbogado = rol.lowercase() == "abogado" || matricula.isNotBlank() || telefono.isNotBlank()

            // 1. ESCRITURA PRINCIPAL EN 'abogados/{uid}'
            // Es vital usar .document(uid).set(..., merge = true) asegurando que 'id' == uid
            if (isAbogado) {
                val abogadoData = mutableMapOf<String, Any>(
                    "id" to uid,
                    "matricula" to matricula,
                    "telefono" to telefono,
                    "zona" to "Rosario",
                    "nivelVerificacion" to NivelVerificacion.NIVEL_2_PENDIENTE.name,
                    "fechaSolicitudVerificacion" to now
                )
                if (email.isNotBlank()) abogadoData["email"] = email

                db.collection("abogados").document(uid).set(abogadoData, merge = true)
            }

            // 2. ESCRITURA SECUNDARIA AISLADA: 'users/{uid}'
            try {
                val userData = mutableMapOf<String, Any>("rol" to rol)
                if (isAbogado) {
                    userData["nivelVerificacion"] = NivelVerificacion.NIVEL_2_PENDIENTE.name
                }
                if (matricula.isNotBlank()) userData["matricula"] = matricula
                if (telefono.isNotBlank()) userData["telefono"] = telefono
                if (email.isNotBlank()) userData["email"] = email

                db.collection("users").document(uid).set(userData, merge = true)
            } catch (e: Exception) {
                println("Aviso: No se pudo actualizar 'users' ($uid) debido a políticas de seguridad: ${e.message}")
            }

            // 3. ESCRITURA SECUNDARIA AISLADA: 'perfiles_abogados/{uid}'
            if (isAbogado) {
                try {
                    db.collection("perfiles_abogados").document(uid).set(
                        mapOf(
                            "abogadoUid" to uid,
                            "matricula" to matricula,
                            "telefono" to telefono,
                            "nivelVerificacion" to NivelVerificacion.NIVEL_2_PENDIENTE.name,
                            "fechaSolicitudVerificacion" to now
                        ),
                        merge = true
                    )
                } catch (e: Exception) {
                    println("Aviso: No se pudo actualizar 'perfiles_abogados' ($uid) debido a políticas de seguridad: ${e.message}")
                }
            }

            OnboardingResult.Success
        } catch (e: Exception) {
            println("Error al guardar en Firestore 'abogados': ${e.message}")
            OnboardingResult.Error(e.message ?: "Error al actualizar el perfil profesional.")
        }
    }

    suspend fun resetearRolUsuario(): OnboardingResult = withContext(Dispatchers.Default) {
        val uid = FirebaseProxy.getCurrentUserId()
            ?: return@withContext OnboardingResult.Error("Usuario no autenticado.")

        try {
            val db = Firebase.firestore
            db.collection("users").document(uid).update("rol" to "indefinido")
            OnboardingResult.Success
        } catch (e: Exception) {
            OnboardingResult.Error(e.message ?: "Error al resetear el rol")
        }
    }
}
